<script>
    import { onMount, setContext } from "svelte";
    import {
        CardLayout, Loader, NotificationModal, PageLoading, SvelteTable,
    } from "../../../Components/Common/index.svelte";
    import {
        addIcon, editIcon, deleteIcon, defaultErrorMsgPrompt,
        listIcon,
    } from "../../../Utils/utilConstants.svelte";
    import { apiMethods } from "../../../Services/index.svelte";
    import {
        getSearchSurgeonOptions, bulkApi, getText, isObjectEmpty, showError, showInfo
    } from "../../../Utils/utilFunctions.svelte";
    import { isEnvNewOrApp } from "../../../Utils/utilRoute.svelte";
    import { isMobile } from "../../../stores";
    import TableFooter from "../../../Components/Layout/Table/TableFooter.svelte";
    import NoGapProgramDetailsAddEdit from "./NoGapProgramDetailsAddEdit.svelte";
    import NoGapProgramItemsRebates from "./NoGapProgramItemsRebates.svelte";
    
    export let hospitalDetails = {};

    $: hospitalName = hospitalDetails?.name
        ? hospitalDetails.name
        : "this hospital";

    const FALLBACK_REBATE_TABLES = [
        {
            key: "Benefit100",
            label: "Medicare Schedule Fee",
            hcpCode: ""
        },
        {
            key: "RebateAHSA",
            label: "RebateAHSA",
            hcpCode: "AUF"
        },
        {
            key: "RebateAHM",
            label: "RebateAHM",
            hcpCode: "AHM"
        },
        {
            key: "RebateMBP",
            label: "RebateMBP",
            hcpCode: "MBP"
        },
        {
            key: "RebateBUPKnownGap",
            label: "RebateBUPKnownGap",
            hcpCode: "BUP"
        },
        {
            key: "RebateBUPNoGap",
            label: "RebateBUPNoGap",
            hcpCode: "BUP"
        },
        {
            key: "RebateCDH",
            label: "RebateCDH",
            hcpCode: "CDH"
        },
        {
            key: "RebateFAI",
            label: "RebateFAI",
            hcpCode: "FAI"
        },
        {
            key: "RebateLHS",
            label: "RebateLHS",
            hcpCode: "LHS"
        },
        {
            key: "RebateMDH",
            label: "RebateMDH",
            hcpCode: "MDH"
        },
        {
            key: "RebateNIBKnownGap",
            label: "RebateNIBKnownGap",
            hcpCode: "NIB"
        },
        {
            key: "RebateNIBNoGap",
            label: "RebateNIBNoGap",
            hcpCode: "NIB"
        },
        {
            key: "RebateSLM",
            label: "RebateSLM",
            hcpCode: "SLM"
        },
        {
            key: "RebateHCFKnownGap",
            label: "RebateHCFKnownGap",
            hcpCode: "HCF"
        },
        {
            key: "RebateHCFNoGap",
            label: "RebateHCFNoGap",
            hcpCode: "HCF"
        },
        {
            key: "RebateTFS",
            label: "RebateTFS",
            hcpCode: "TFS"
        }
    ]
    setContext("FALLBACK_REBATE_TABLES", FALLBACK_REBATE_TABLES);

    const getRuleDescription = (ruleGroup) => {
        return `${ruleGroup.rebateType == 2 ? 'Uplift with' : 'Unit value of'} ${ruleGroup.rebateType == 2 ? `${ruleGroup.upliftRebate == "" ? "Default fees" : ruleGroup.upliftRebate}` : `$${ruleGroup.unitValue}`} (${ruleGroup.itemNums.map(i => i).join(", ")})`;
    }

    const getDefaultRuleDescription = (noGapProgram) => {
    if (noGapProgram.rebateType == 0) {
        return `Not Set, Fallback: ${noGapProgram.fallBackRebate}`;
    }

    return `${noGapProgram.rebateType == 2 ? 'Uplift' : 'Unit value of'} ${noGapProgram.rebateType == 2
        ? `(${noGapProgram.upliftRebate === "" ? "Default fees" : noGapProgram.upliftRebate})`
        : `$${noGapProgram.unitValue}`}, Fallback: ${noGapProgram.fallBackRebate}`;
    };

    $: columns = [
        {
            key: "fundId",
            title: "Insurance Fund",
            headerClass: `text-left pl-4 w-1/6`,
            class: "text-left pl-4 cursor-default align-middle",
            renderValue: (v) => getText(v.fundName, "-"),
        },
        {
            key: "surgeon",
            title: "Surgeons",
            headerClass: "text-left w-1/5 pl-4 cursor-default",
            class: "text-left pl-4 cursor-default align-middle ",
            renderValue: (v) => v.isAllProviders ? "All surgeons" : 
                v.hospitalsNoGapProgramProviders.length > 0 ? 
                    v.hospitalsNoGapProgramProviders.map(i => i.providerName).join(", ") : "No surgeons selected"
        },
        {
            key: "specialRules",
            title: "Special Rules",
            headerClass: "text-left pl-4 cursor-default",
            class: "text-left pl-4 cursor-default align-middle ",
            renderValue: (v) =>  {
                if(v.hospitalsNoGapProgramSpecialRule && v.hospitalsNoGapProgramSpecialRule.length > 0) {
                    let groupedRules = groupRules(v.hospitalsNoGapProgramSpecialRule);
                    return groupedRules.map(r => getRuleDescription(r)).join(", ");

                } else {
                    return 'No special rules'
                }
            }
        },
        {
            key: "defaultRule",
            title: "Default Rule",
            headerClass: "text-left w-1/6 pl-4 cursor-default",
            class: "text-left pl-4 cursor-default align-middle ",
            renderValue: (v) => getDefaultRuleDescription(v)
        },
        {
            key: "upliftInvoice",
            title: "Is Uplift Invoice",
            headerClass: "text-left w-1/12 pl-4 ",
            class: "text-left pl-4 cursor-default align-middle",
            renderValue: (v) => v.isUpliftInvoice ? "Yes" : "No",
        },
        {
            key: "upliftInvoiceEmail",
            title: "Invoice Email",
            headerClass: `text-left w-1/12 pl-4 cursor-default ${isEnvNewOrApp() ? "w-1/12" : "w-1/6"}`,
            class: "text-left pl-4 cursor-default align-middle",
            renderValue: (v) => getText(v.upliftInvoiceEmail, "")
                
        },
        {
            key: "actions",
            title: "Action",
            headerClass: "text-left pl-2 w-[120px] cursor-default",
            class: "text-left w-[120px] cursor-default align-middle",
            renderValue: (v) => _renderActionIcons(),
            isHidden: !isEnvNewOrApp(),
        },
    ];

    const _renderActionIcons = () =>
        `<div class="flex flex-1 items-center justify-start gap-x-2">
                <div class="listIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center cursor-pointer w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="View No Gap Program Rebates"}">
                    <i class="listIcon ${listIcon}"></i>
                </div>
                <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center cursor-pointer w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit No Gap Program"}">
                    <i class="editIcon ${editIcon}"></i>
                </div>
                <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center cursor-pointer w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Remove No Gap Program"}">
                    <i class="deleteIcon ${deleteIcon}"></i>
                </div>
           </div>
        `;

    let isLoadingNoGapProgramDetails = false,
        noGapPrograms = [],
        filters = {},
        mbsItems = [];

    let isModifyModalOpen = false,
        modifyType = "",
        isUpdatingNoGroupProgram = false,
        updatingStatus = {},
        noGapProgramDetails = {};

    let page = 0,
        pageSize = 25,
        count;

    let isLoadingInsuranceFunds = false,
        isLoadingSurgeons = false,
        noGapInsuranceFunds = [],
        noGapSurgeons = [],
        selectedInsuranceFund,
        selectedRebateTable,
        selectedSurgeons = [],
        specialRules = [],
        defaultRule = {},
        allSurgeonsSelected = true,
        isUpliftInvoice = false,
        fallbackRebateTable,
        upliftInvoiceEmail,
        stepIndex = 0;
    
    let isShowingItemRebates = false;
    let selectedNoGapProgramDetails;
    
    let addEditFormRef;


    const btnOptions = [
        {
            iconName: "addIcon",
            iFont: addIcon,
            iconFunction: () => _openModifyNoGroupProgramModal("Add"),
            hasTooltip: false,
            btnText: "Add",
            isHidden: !isEnvNewOrApp(),
        },
    ];

    onMount(() => {
        initNoGapPrograms();

        loadNoGapInsuranceFunds();
        loadMbsItems();

    });

    const initNoGapPrograms = async (
        start = page,
        limit = pageSize,
        params = _getSearchParams(),
    ) => {
        isLoadingNoGapProgramDetails = true;

        await apiMethods.getMethod
            .getHospitalsNoGapProgram(start, limit, params)
            .then((res) => {
                _handleNoGapProgramsResponse(res);
            });

        isLoadingNoGapProgramDetails = false;
    };

    const loadMbsItems = async () => {
        //only T7 and T10
        var mbsResponse = await apiMethods.getMethod.getMBSItems(0, 999999, { "filter[group]": "T10,T7" });
        if (!mbsResponse || mbsResponse.error) {
            showError(
                mbsResponse.error ??
                    `Failed to load T10 and T7 MBS Items. ${defaultErrorMsgPrompt}`,
            );
            return;
        } else {
            mbsItems = mbsResponse.results;
        }
    }

    const loadNoGapInsuranceFunds = async () => {
        isLoadingInsuranceFunds = true;

        await apiMethods.getMethod
            .getInsuranceFund(0, 999999, {
                "": Number(1),
            })
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            `Failed to load no gap insurance funds. ${defaultErrorMsgPrompt}`,
                    );
                else
                    noGapInsuranceFunds = res.results.map((fund) => ({
                        key: fund.id,
                        value: fund.name,
                        hcpCode: fund.hcpCode
                    }));
            })
            .catch((err) => showError(err));

        isLoadingInsuranceFunds = false;
    };

    const handleSearchSurgeon = async (searchText = "", filterBySpecialityCodes) => {
        if (!searchText || searchText.length < 2) return [];

        isLoadingSurgeons = true;

        await apiMethods.getMethod
            .getAllProviders(0, 999999, { 
                name: searchText, 
                state: hospitalDetails.state,
                specialitycode: filterBySpecialityCodes === true ? "4,5,7,31,32,33,34,35,36,37,38,39,42,46,52,53,54,55,56,62,73,84,85,87,99,110,153,157,158,606,611,808" : ""
            })
            .then((res) => {
                if (!res.error) _filterSurgeon(res.results);
                else noGapSurgeons = [];
            });

        isLoadingSurgeons = false;
    };

    const _filterSurgeon = (arr = []) => {
        if (!arr || !arr.length) return [];

        noGapSurgeons = getSearchSurgeonOptions(arr, false);
    };
  
    const loadNoGapPrograms = async (
        start = page,
        limit = pageSize,
        params = _getSearchParams(),
    ) => {
        await apiMethods.getMethod
            .getHospitalsNoGapProgram(start, limit, params)
            .then((res) => {
                _handleNoGapProgramsResponse(res);
            })
            .then(() => _updateHeaderSortIcon());
    };

    const groupRules = (data) => {
        return Object.values(data.reduce((acc, item) => {
            let key;

            if (item.rebateType === 1) {
                key = `type1-${item.unitValue}`;
            } else {
                key = `type0-${item.upliftRebate}`;
            }

            if (!acc[key]) {
                acc[key] = {
                    hospitalNoGapId: item.hospitalNoGapId,
                    rebateType: item.rebateType,
                    unitValue: item.unitValue,
                    upliftRebate: item.upliftRebate,
                    error: item.error ?? null,
                    itemNums: []
                };
            }

            acc[key].itemNums.push(item.itemNum);
            return acc;
        }, {}));
    }

    const handleModifyNoGapProgram = async () => {
        switch (modifyType) {
            case "ADD":

                if(await addEditFormRef.checkNoGapProgramValidation()) {
                    isUpdatingNoGroupProgram = true;
                    await createNoGapProgram();
                } else {
                    showInfo("Please make sure all mandatory fields are provided.");
                    return;
                }
                break;
            case "EDIT":
                if(await addEditFormRef.checkNoGapProgramValidation()) {
                    isUpdatingNoGroupProgram = true;
                    await updateNoGapProgram();
                } else {
                    showInfo("Please make sure all mandatory fields are provided.");
                    return;
                }
                break;
            case "DELETE":
                await deleteNoGapProgram();
                break;
            default:
                break;
        }

        if (modifyType != "ADD") {
            isUpdatingNoGroupProgram = false;

            _closeModifyNoGroupProgramModal();
        }
        
    };

    const createNoGapProgram = async () => {
       
        //hospital no gap program data
        const data = {
            hospitalId: hospitalDetails.id,
            fundId: selectedInsuranceFund.key,
            isAllProviders: allSurgeonsSelected,
            isUpliftInvoice: isUpliftInvoice,
            upliftInvoiceEmail: isUpliftInvoice ? upliftInvoiceEmail.trim() : null,
            rebateType: !isObjectEmpty(defaultRule) ? defaultRule.rebateType : null,
            unitValue: !isObjectEmpty(defaultRule) ? defaultRule.unitValue : 0,
            upliftRebate: !isObjectEmpty(defaultRule) ? defaultRule.upliftRebate : null,
            fallBackRebate: fallbackRebateTable
        };
        var noGapProgramResponse = await apiMethods.postMethod.createHospitalIsNoGapProgram(data);
        
        if (!noGapProgramResponse || noGapProgramResponse.error)
            showError(
                noGapProgramResponse.error ??
                    `Failed to create No-Gap Program. ${defaultErrorMsgPrompt}`,
            );

        if(noGapProgramResponse?.id){
            if(!allSurgeonsSelected && selectedSurgeons.length > 0){
                //save hospital no gap program providers
                const apiRequests = [];
            
                for (const surgeon of selectedSurgeons) {
                    const noGapProgramSurgeon = {
                        hospitalNoGapId: noGapProgramResponse.id,
                        providerUniqueId: surgeon.key,
                    };
                    apiRequests.push(() =>
                        apiMethods.postMethod.addHospitalNoGapProgramSurgeon(noGapProgramSurgeon)
                    );
                }

                await bulkApi(
                    apiRequests,
                    ({ }) =>
                        (updatingStatus = {
                            isRunning: true,
                        }),
                )
                .catch((error) => showError(error));
            } 

            if(specialRules.length > 0) {

                const apiRequests = [];

                for (const rule of specialRules) {
                    for(const item of rule.itemNums){
                        const addRuleData = {
                            ...rule,
                            itemNum: item,
                            hospitalNoGapId: noGapProgramResponse.id
                        }
                        
                        apiRequests.push(() =>
                            apiMethods.postMethod.addHospitalNoGapProgramRule(addRuleData)
                        );
                    }
                }

                await bulkApi(
                    apiRequests,
                    ({ }) =>
                        (updatingStatus = {
                            isRunning: true,
                        })

                )
                .catch((error) => showError(error));
            }

            //no surgeons to be saved
            updatingStatus = { isRunning: false, success: true, message: "No Gap Program was successfully created." }

        }
       
    };

    const updateNoGapProgram = async () => {

        const data = {
            ...noGapProgramDetails,
            fundId: selectedInsuranceFund.key,
            isAllProviders: allSurgeonsSelected,
            isUpliftInvoice: isUpliftInvoice,
            upliftInvoiceEmail: isUpliftInvoice ? upliftInvoiceEmail : null,
            rebateType: !isObjectEmpty(defaultRule) ? defaultRule.rebateType : null,
            unitValue: !isObjectEmpty(defaultRule) ? defaultRule.unitValue : 0,
            upliftRebate: !isObjectEmpty(defaultRule) ? defaultRule.upliftRebate : null,
            fallBackRebate: fallbackRebateTable
        };

        var noGapProgramResponse = await apiMethods.putMethod.updateHospitalIsNoGapProgram(data);
        if (!noGapProgramResponse || noGapProgramResponse.error)
            showError(
                noGapProgramResponse.error ??
                    `Failed to create No-Gap Program. ${defaultErrorMsgPrompt}`,
            );
        
        if(noGapProgramResponse?.id){ 
            if(allSurgeonsSelected && noGapProgramDetails.hospitalsNoGapProgramProviders.length > 0) {
                //remove all existing providers linked to this no gap program
                const apiRequests = [];
                
                for (const surgeonProvider of noGapProgramDetails.hospitalsNoGapProgramProviders) {
                    apiRequests.push(() =>
                        apiMethods.deleteMethod.deleteHospitalIsNoGapProgramProvider(surgeonProvider)
                    );
                }

                await bulkApi(
                    apiRequests,
                    ({ }) =>
                        (updatingStatus = {
                            isRunning: true,
                        }),
                )
                .catch((error) => showError(error));

            } else if (!allSurgeonsSelected) {
                
                //surgeons that are not selected anymore
                var surgeonsToDelete = noGapProgramDetails.hospitalsNoGapProgramProviders.filter(p => !selectedSurgeons.map(s => s.key).includes(p.providerUniqueId));

                //new surgeons added
                var surgeonsToAdd = selectedSurgeons.filter(s => !noGapProgramDetails.hospitalsNoGapProgramProviders.map(p => p.providerUniqueId).includes(s.key));

                const apiRequests = [];
                
                for (const toDelete of surgeonsToDelete) {
                    apiRequests.push(() =>
                        apiMethods.deleteMethod.deleteHospitalIsNoGapProgramProvider(toDelete)
                    );
                }

                for (const toAdd of surgeonsToAdd) {
                    const noGapProgramSurgeon = {
                        hospitalNoGapId: noGapProgramResponse.id,
                        providerUniqueId: toAdd.key,
                    };
                    apiRequests.push(() =>
                        apiMethods.postMethod.addHospitalNoGapProgramSurgeon(noGapProgramSurgeon)
                    );
                }

                await bulkApi(
                    apiRequests,
                    ({ }) =>
                        (updatingStatus = {
                            isRunning: true,
                        }),
                )
                .catch((error) => showError(error));

            }

            //delete all existing hospital rules
            var deleteResponse = await apiMethods.deleteMethod.deleteHospitalIsNoGapProgramRules(noGapProgramResponse?.id);
            if (!deleteResponse || deleteResponse.error) {
                showError(
                    noGapProgramResponse.error ??
                        `Failed to update hospital no gap program rules.`,
                );
            } else {
                //add the rules again
                if(specialRules.length > 0){
                    const apiRequests = [];
                    for (const rule of specialRules) {
                        for(const item of rule.itemNums){
                            const addRuleData = {
                                ...rule,
                                itemNum: item,
                                hospitalNoGapId: noGapProgramResponse.id
                            }
                            
                            apiRequests.push(() =>
                                apiMethods.postMethod.addHospitalNoGapProgramRule(addRuleData)
                            );
                        }
                    }

                    await bulkApi(
                        apiRequests,
                        ({ }) =>
                            (updatingStatus = {
                                isRunning: true,
                            }),
                    )
                    .catch((error) => showError(error));
                }

            }

            updatingStatus = { isRunning: false, success: true, message: "No Gap Program was successfully updated." }
        }
            
    };

    const deleteNoGapProgram = async (reloadAfterFinish = true) =>
        await apiMethods.deleteMethod
            .deleteHospitalIsNoGapProgram(noGapProgramDetails)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            `Failed to remove selected No-Gap Program. ${defaultErrorMsgPrompt}`,
                    );
                else reloadAfterFinish && loadNoGapPrograms();
    });

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        loadNoGapPrograms(Number(e.detail.firstDataIndex), pageSize, filters);
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        loadNoGapPrograms(0, pageSize, filters);
    };

    // HELPER FUNCTIONS
    const _handleNoGapProgramsResponse = (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    `Failed to load existing No-Gap Program hospitals. ${defaultErrorMsgPrompt}`,
            );
        else {
            noGapPrograms = [...res.results];
            count = res.total;
        }
    };

    const _getSearchParams = () => {
        let params = { "filter[hospitalId]": hospitalDetails.id };

        if (filters.sort) params.sort = filters.sort;

        if (filters.providerUniqueId)
            params["filter[providerUniqueId]"] = filters.providerUniqueId;

        if (filters.insuranceFundId)
            params["filter[fundId]"] = filters.insuranceFundId;

        if (filters.itemNum) params["filter[itemNum]"] = filters.itemNum;

        return params;
    };

    const _openModifyNoGroupProgramModal = async (type = "") => {
        modifyType = type.toUpperCase();

        if (!type || !["ADD", "EDIT", "DELETE"].includes(type.toUpperCase())) {
            modifyType = "";
            return;
        }

        isModifyModalOpen = true;

        switch (modifyType) {
            case "ADD":
                selectedSurgeons = [];
                selectedInsuranceFund = null;
                selectedRebateTable = null;
                upliftInvoiceEmail = null;
                allSurgeonsSelected = true;
                isUpliftInvoice = false;
                specialRules = [];
                break;
            case "EDIT":
                //set edit details
                allSurgeonsSelected = noGapProgramDetails.isAllProviders;
                isUpliftInvoice = noGapProgramDetails.isUpliftInvoice;
                upliftInvoiceEmail = noGapProgramDetails.upliftInvoiceEmail;
                fallbackRebateTable = noGapProgramDetails.fallBackRebate;
                defaultRule = {
                    rebateType: noGapProgramDetails.rebateType,
                    unitValue: noGapProgramDetails.unitValue,
                    upliftRebate: noGapProgramDetails.upliftRebate != null && noGapProgramDetails.upliftRebate != undefined ? noGapProgramDetails.upliftRebate : null
                };

                //preselect fund
                selectedInsuranceFund = noGapInsuranceFunds.find(f => f.key == noGapProgramDetails.fundId);
                //preselect rebate table
                if(noGapProgramDetails.fallBackRebate != null && noGapProgramDetails.fallBackRebate != ""){
                    selectedRebateTable = FALLBACK_REBATE_TABLES.find(r => r.key == noGapProgramDetails.fallBackRebate);
                }

                //preselect surgeons
                if(!allSurgeonsSelected && noGapProgramDetails.hospitalsNoGapProgramProviders.length > 0) {
                    selectedSurgeons = noGapProgramDetails.hospitalsNoGapProgramProviders.map((hp) => ({
                        key: hp.providerUniqueId,
                        value: hp.providerName
                    }));
                   
                }

                //preselect special rules
                specialRules = groupRules(noGapProgramDetails.hospitalsNoGapProgramSpecialRule);
               
                break;
            case "DELETE":
            default:
                break;
        }
    };

    const _closeModifyNoGroupProgramModal = () => {
        modifyType = "";
        isModifyModalOpen = false;

        selectedSurgeons = [];
        specialRules = [];
        defaultRule = {};
        selectedInsuranceFund = null;
        selectedRebateTable = null;
        isUpliftInvoice = false;
        allSurgeonsSelected = true;
        upliftInvoiceEmail = null;
        fallbackRebateTable = null;
        noGapSurgeons = [];

        noGapProgramDetails = {}; //reset selection
        stepIndex = 0;
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");

                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                Array.from(thRow).forEach((row) => {
                    const existingSpans = row.querySelectorAll("span");
                    existingSpans.forEach((span) => row.removeChild(span));

                    if (
                        (params.sort == "itemNum" &&
                            row.childNodes[0].data == "Item Number") ||
                        (params.sort == "fundId" &&
                            row.childNodes[0].data == "Insurance Fund")
                    ) {
                        row.appendChild(upSpan);
                    } else if (
                        (params.sort == "itemNumDesc" &&
                            row.childNodes[0].data == "Item Number") ||
                        (params.sort == "fundIdDesc" &&
                            row.childNodes[0].data == "Insurance Fund")
                    ) {
                        row.appendChild(downSpan);
                    }
                });
            }, 0);
        }
    };

    const _openShowItemRebates = (noGapProgram) => {
        isShowingItemRebates = true;
        selectedNoGapProgramDetails = noGapProgram;
    }

    const _closeShowItemRebates = () => {
        isShowingItemRebates = false;
        selectedNoGapProgramDetails = null;
    }

    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        if (key == "actions") {
            noGapProgramDetails = { ...rowData };

            if (className.includes("editIcon"))
                _openModifyNoGroupProgramModal("Edit");
            else if (className.includes("deleteIcon"))
                _openModifyNoGroupProgramModal("Delete");
            else if (className.includes("listIcon"))
                _openShowItemRebates(noGapProgramDetails);
        } 
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {
       /*  if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}Desc`;
            else filters.sort = key;
            loadNoGapPrograms(0, pageSize, _getSearchParams());
        } */
    };

</script>

<CardLayout title="No Gap Programs" {btnOptions}>
    <div class="flex w-full flex-1 p-4">
        <div
            class={`flex flex-col gap-2 h-auto ${$isMobile ? "w-minus48" : "relative flex-1"}`}
        >
            {#if isLoadingNoGapProgramDetails}
                <PageLoading />
            {:else if !noGapPrograms.length}
                <p class="w-full">
                    No No-Gap Program records currently under {hospitalName}.
                </p>
            {:else}
                <div
                    class={$isMobile
                        ? "overflow-x-auto overflow-y-visible"
                        : "overflow-y-auto max-h-[800px]"}
                >
                    <SvelteTable
                        columns={columns.filter((col) => !col.isHidden)}
                        rows={noGapPrograms}
                        clickCellFunc={({ detail: { event, row, key } }) => {
                            handleClickCell(event, row, key);
                        }}
                        clickColFunc={({ detail: { event, row, key } }) => {
                            handleClickCol(event, row, key);
                        }}
                    />
                </div>

                <TableFooter
                    {page}
                    {pageSize}
                    {count}
                    tableData={noGapPrograms}
                    {onPageChange}
                    {onPageSizeChange}
                />
            {/if}
        </div>
    </div>
</CardLayout>

<NotificationModal
    isModalOpen={isModifyModalOpen && modifyType}
    title={`${modifyType} No-Gap Program`}
    closeModalFunction={_closeModifyNoGroupProgramModal}
    showCancelBtn={false}
    showConfirmBtn={false}
    modalMaxWidth={'w-[800px] max-w-4xl'}
    btnOptions={[
        {
            text: "Cancel",
            class: "btn-accent",
            loading: isUpdatingNoGroupProgram,
            function: () => _closeModifyNoGroupProgramModal(),
        },
        (modifyType == "ADD" || modifyType == "EDIT") && stepIndex <= 2 ? 
        {
            text: "Next",
            class: "btn-secondary",
            function: async () => {
                if(await addEditFormRef.checkNoGapProgramValidation(stepIndex)) {
                    stepIndex++;
                }
            },
        }
        :
        {
            text: isUpdatingNoGroupProgram ? "Updating" : "Confirm",
            class: "btn-secondary",
            function: () => handleModifyNoGapProgram(),
        },
    ]}
>
    <div class="flex flex-col flex-1 p-4 gap-y-4 ">
        {#if modifyType == "ADD" || modifyType == "EDIT"}
            <NoGapProgramDetailsAddEdit
                hospitalId={hospitalDetails.id}
                handleSearchSurgeon={handleSearchSurgeon}
                bind:stepIndex={stepIndex}
                bind:this={addEditFormRef}
                bind:selectedInsuranceFund={selectedInsuranceFund}
                noGapInsuranceFunds={noGapInsuranceFunds}
                bind:allSurgeonsSelected={allSurgeonsSelected}
                bind:selectedSurgeons={selectedSurgeons}
                bind:isUpliftInvoice={isUpliftInvoice}
                bind:upliftInvoiceEmail={upliftInvoiceEmail}
                noGapSurgeons={noGapSurgeons}
                bind:specialRules={specialRules}
                bind:defaultRule={defaultRule}
                bind:selectedRebateTable={selectedRebateTable}
                bind:fallbackRebateTable={fallbackRebateTable}
                mbsItems={mbsItems}
            >

            </NoGapProgramDetailsAddEdit>
       
        {:else if modifyType == "DELETE"}
            <p>
                You are about to delete selected No-Gap Program from {hospitalName}.
            </p>
            <p>Are you sure to confirm this action?</p>
        {/if}
    </div>
</NotificationModal>

<NotificationModal
    title="Creation Done:"
    isModalOpen={!isObjectEmpty(updatingStatus)}
    showCancelBtn={false}
    showConfirmBtn={!updatingStatus?.isRunning}
    submitText="Close"
    confirmAction={async () => {
        updatingStatus = {};
        isUpdatingNoGroupProgram = false;
        _closeModifyNoGroupProgramModal();
        await loadNoGapPrograms();
    }}
>
    <div class="flex flex-col flex-1 justify-center p-4 gap-y-4">
        {#if updatingStatus.isRunning}
            <div class="flex w-full items-center gap-x-4">
                <Loader />
                <p>Creating No-Gap Program...</p>
            </div>
        {:else if updatingStatus.success}
            <p>{updatingStatus.message ?? "Operation finished successfully"}</p>
        {/if}
    </div>
</NotificationModal>

<NotificationModal
    isModalOpen={isShowingItemRebates}
    title={`No-Gap Program Items Rebates`}
    closeModalFunction={_closeShowItemRebates}
    showCancelBtn={false}
    showConfirmBtn={false}
    modalMaxWidth={'w-[1000px] max-w-6xl'}
    btnOptions={[
        {
            text: "Close",
            class: "btn-accent",
            function: _closeShowItemRebates,
        },
    ]}
>
    <NoGapProgramItemsRebates noGapProgramDetails={selectedNoGapProgramDetails}></NoGapProgramItemsRebates>
</NotificationModal>
